import { useState, useEffect } from "preact/hooks";
import { appStore } from "./store.ts";
import { useStore } from "./useStore.ts";

export function PairView() {
  const connected = useStore(appStore, (s) => s.connected);
  const pairing = useStore(appStore, (s) => s.pairing);
  const pairError = useStore(appStore, (s) => s.pairError);
  const roomStatus = useStore(appStore, (s) => s.roomStatus);

  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [localError, setLocalError] = useState("");

  // Request room status each time the view is shown while connected
  useEffect(() => {
    if (connected) {
      appStore.getState().requestRoomStatus();
    }
  }, [connected]);

  // Reset code entry view when connection state changes
  useEffect(() => {
    if (connected) {
      setShowCodeEntry(false);
      setPairCode("");
      setLocalError("");
    }
  }, [connected]);

  function handlePair() {
    const code = pairCode.trim().toUpperCase();
    if (code.length !== 7) {
      setLocalError("Enter a 7-character code");
      return;
    }
    setLocalError("");
    appStore.getState().pair(code);
  }

  function handleSwitchPlayer() {
    appStore.getState().disconnect();
    setShowCodeEntry(true);
  }

  const displayError = localError || pairError;

  // Show code entry when not connected or user chose to switch
  if (!connected || showCodeEntry) {
    return (
      <div style={{ textAlign: "center", marginTop: "40px" }}>
        <h3>Enter Connection Code</h3>
        <input
          value={pairCode}
          onInput={(e) =>
            setPairCode((e.target as HTMLInputElement).value.toUpperCase().replace(/[^A-Z]/g, ""))
          }
          onKeyDown={(e) => e.key === "Enter" && handlePair()}
          maxLength={7}
          placeholder="ABCDEFG"
          style={{
            fontSize: "32px",
            letterSpacing: "8px",
            textAlign: "center",
            width: "100%",
            padding: "12px",
            marginBottom: "12px",
            fontFamily: "monospace",
            textTransform: "uppercase",
            boxSizing: "border-box",
          }}
        />
        <button
          onClick={handlePair}
          disabled={pairing}
          style={{
            padding: "12px 24px",
            fontSize: "16px",
            width: "100%",
          }}
        >
          {pairing ? "Connecting..." : "Connect"}
        </button>
        {displayError && (
          <p style={{ color: "red", marginTop: "8px" }}>{displayError}</p>
        )}
      </div>
    );
  }

  // Connected — show room status
  return (
    <div style={{ marginTop: "20px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <h3 style={{ margin: 0 }}>
          {roomStatus?.roomName || "Connected"}
        </h3>
        <span
          style={{
            display: "inline-block",
            width: "10px",
            height: "10px",
            borderRadius: "50%",
            background: roomStatus?.playerOnline ? "#4caf50" : "#f44336",
            marginLeft: "8px",
          }}
          title={roomStatus?.playerOnline ? "Player online" : "Player offline"}
        />
      </div>

      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "12px 16px",
          marginBottom: "16px",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            color: "#666",
            marginBottom: "8px",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          Player
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: roomStatus?.playerOnline ? "#4caf50" : "#f44336",
              flexShrink: 0,
            }}
          />
          <span>{roomStatus?.playerOnline ? "Online" : "Offline"}</span>
        </div>
      </div>

      {roomStatus && roomStatus.controllers.length > 0 && (
        <div
          style={{
            background: "#f5f5f5",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: "#666",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Controllers ({roomStatus.controllers.length})
          </div>
          {roomStatus.controllers.map((c, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "4px 0",
              }}
            >
              <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  background: c.connected ? "#4caf50" : "#999",
                  flexShrink: 0,
                }}
              />
              <span>{c.name || "Unnamed controller"}</span>
              {!c.connected && (
                <span style={{ color: "#999", fontSize: "12px" }}>
                  (disconnected)
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={handleSwitchPlayer}
        style={{
          padding: "10px 20px",
          fontSize: "14px",
          width: "100%",
          marginTop: "8px",
          background: "none",
          border: "1px solid #ccc",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        Connect to a different player
      </button>
    </div>
  );
}
